// @ts-strict-ignore
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectModel } from '@project/model/project.model';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { ProjectArticleNumberInfoVO, ProjectSaveInfoVO, ProjectVO } from '@project/model/vo/project.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectStatesDef } from '@project/components/project/constants/project-states-def';
import { IsProjectType } from '@project/model/is-project-type';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { ProjectDataService } from '@project/service/project-data.service';
import { ProjectComponentPresenter } from '../../../project-component.presenter';
import { UserUtils } from '@user/service/user-utils';
import { UserModel } from '@user/model/user.model';
import { ArticleNumberLong } from '@project/model/article-number-long';
import { FinalizeProjectComponent } from '@project/components/project/finalize-project/finalize-project.component';

@Component({
  standalone: false,
  selector: 'app-choose-finalization-action',
  templateUrl: './choose-finalization-action.component.html',
})
export class ChooseFinalizationActionComponent implements OnInit, OnDestroy {

  protected static readonly FINALIZATION_ACTION_NORMAL_EXPORT: number = 1;
  protected static readonly FINALIZATION_ACTION_LEGACY_EXPORT: number = 2;
  protected static readonly FINALIZATION_ACTION_NO_PDM_EXPORT: number = 3;

  public readonly SELECTED_ACTION_FINALIZE_PROJECT: string = 'FINALIZE_PROJECT';
  public readonly SELECTED_ACTION_FINALIZE_LEGACY_PROJECT: string = 'FINALIZE_LEGACY_PROJECT';
  public readonly SELECTED_ACTION_SET_TO_CLOSED: string = 'SET_TO_CLOSED';
  public readonly SELECTED_ACTION_SET_TO_LOCKED: string = 'SET_TO_LOCKED';
  public readonly SELECTED_ACTION_SET_TO_UNLOCKED: string = 'SET_TO_UNLOCKED';
  public readonly SELECTED_ACTION_GENERATE_PDM_ARTICLE_NUMBER: string = 'GENERATE_PDM_ARTICLE_NUMBER';

  @Output() public finish = new EventEmitter<void>();

  private _selectedAction: string;
  public isLocked: boolean;
  private _projectSaveInfo: ProjectSaveInfoVO;
  private _lastFinalizationState: number;

  public isLegacyOptionAvailable: boolean = false;
  public showExportWithoutPdm = false;

  constructor(
    private bsModalRef: BsModalRef,
    private localeResourceManager: LocaleResourceManager,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private projectModel: ProjectModel,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private alertHelper: AlertHelper,
    private projectComponentPresenter: ProjectComponentPresenter,
    private userModel: UserModel,
  ) { }

  public ngOnInit(): void {
    this.showExportWithoutPdm = !IsProjectType.controlCabinet(this.projectModel.currentProjectType) || this.userModel.user.isAdmin();
    this.isLocked = this.projectModel.currentProject.isLocked;
    this.projectDataService.loadProjectSaveInfo(this.projectModel.currentProject.id).subscribe(
      (saveInfo: ProjectSaveInfoVO) => this._projectSaveInfo = saveInfo,
      (error: any) => this._projectSaveInfo = null
    );

    this.projectDataService.getLastSuccessfulFinalizationCompleteState(this.projectModel.currentProject.id).subscribe(
      (finalisationState: number) => this.lastFinalizationState = finalisationState,
      (error: any) => this._projectSaveInfo = null
    );
    // this.selectedAction = 'finalizeProject';
    this.isLegacyOptionAvailable = this.isLegacyOptionsAvailable();
  }

  public ngOnDestroy(): void {
  }

  public set selectedAction(value: string) {
    this._selectedAction = value;
  }

  public get selectedAction(): string {
    return this._selectedAction;
  }

  public set lastFinalizationState(value: number) {
    this._lastFinalizationState = value;
  }

  public get lastFinalizationState(): number {
    return this._lastFinalizationState;
  }

  public close(closeTypeSuccess: boolean = false): void {
    this.bsModalRef.hide();
  }

  public async nextButtonClickHandler(): Promise<void> {
    if (this.selectedAction === this.SELECTED_ACTION_FINALIZE_PROJECT ) {
      await this.continueFinalizeProject();
    } else if (this.selectedAction === this.SELECTED_ACTION_FINALIZE_LEGACY_PROJECT) {
      await this.executeFinalizeLegacyProject();
    } else if (this.selectedAction === this.SELECTED_ACTION_SET_TO_CLOSED) {
      await this.executeFinalizeProjectWithoutPdmExport();
    } else if (this.selectedAction === this.SELECTED_ACTION_SET_TO_UNLOCKED) {
      await this.unlockProject();
    } else if (this.selectedAction === this.SELECTED_ACTION_SET_TO_LOCKED) {
      await this.lockProject();
    } else if (this.selectedAction === this.SELECTED_ACTION_GENERATE_PDM_ARTICLE_NUMBER) {
      if (IsProjectType.ltl(this.projectModel.currentProjectType) || IsProjectType.splicingConnector221(this.projectModel.currentProjectType)) {
        this.generatePdmArticleNumber(true);
      } else {
        this.openChoiceActionManualArticleNoPopup();
      }
    }
  }

  public async continueFinalizeProject(): Promise<void> {
    if ( this.isConfigurationCancelled() ||
      ! (await this.confirmProjectUnlocked()) ||
      ! (await this.updateProjectVersion()) ||
      ! (await this.switchLegacyFinalization())) {
      return;
    }

    if (
      this.projectModel.currentProject
      &&  this.projectModel.currentProject.articleNumberLong
      && !this.projectModel.currentProject.articleNumberLong.isEmpty()
    ) {
      if (this.projectModel.currentProject.finalizationState !== ProjectStatesDef.FINALIZATION_STATE_PDM_EXPORT_DONE
        && this.projectModel.currentProject.finalizationState !== ProjectStatesDef.FINALIZATION_STATE_LEGACY_PROJECT_EXPORT_DONE
        && this.projectModel.currentProject.finalizationState !== ProjectStatesDef.FINALIZATION_STATE_FINALIZED_WITHOUT_PDM
        && this.projectModel.currentProject.documentVersion < 0 && this.projectModel.currentProject.isArticleNumberGenerated) {
        await this.prepareFinalization();
      } else {
        this.alertHelper.showAlert('finalizeProject.title.articleNumberLongExists', 'finalizeProject.message.articleNumberLongExists', { 0: this.projectModel.currentProject.articleNumberLong.reducedArticleNumberLong},
        [AlertButton.YES, AlertButton.NO], "error").then(async (answer) => {
          if (answer === AlertButton.YES) {
            await this.prepareFinalization();
          }
        });
      }
    } else {
      await this.prepareFinalization();
    }
  }

  private async prepareFinalization() {
    if (this.projectModel.currentProjectRequiresSaving) {
      await this.projectNavigationPresenter.openSaveProjectDialog();
      if (!this.projectModel.currentProjectRequiresSaving) {
        this.continueFinalizeHandler();
      }
    } else {
      this.continueFinalizeHandler();
    }
  }

  public continueFinalizeHandler(): void {
    if (this.projectModel.currentProjectType === "MARKINGSYSTEM" && (isNaN(this.projectModel.currentProject.documentVersion) || this.projectModel.currentProject.documentVersion < 0)) {
      this.showFinalizeProjectPopup();
    } else if (this.projectModel != null && !StringUtils.isEmpty(this.projectModel.currentProject.typeClassId)
      && !StringUtils.isEmpty(this.projectModel.currentProject.articleNumber) && !StringUtils.isEmpty(this.projectModel.currentProject.articleNumberLong.reducedArticleNumberLong)) {
      // Maybe documentVersion (but document version could be reset) could be used in the check regarding repetition.
      // With automatically generated article numbers this check works fine at the moment, because no typeClassId is automatically set
      this.close();
      this.projectComponentPresenter.showFinalizeRepetitionPopup(this.projectModel.currentProject);
    } else {
      this.showFinalizeProjectPopup();
    }
  }

  public showFinalizeProjectPopup(): void {
    this.close();
    const data: Partial<FinalizeProjectComponent> = {
      mcsGds221LtlExport: IsProjectType.among(this.projectModel.currentProjectType, 'MCS', 'GDS', 'SPLICING_CONNECTOR_221', 'LTL'),
      useCustomArticleNumber: this.projectModel.currentProject.articleNumberLong && !this.projectModel.currentProject.articleNumberLong.isEmpty(),
    };
    this.projectComponentPresenter.showFinalizeProjectPopup(data);
  }

  public async executeFinalizeLegacyProject(): Promise<void> {
    if ( this.isConfigurationCancelled() ||
      !(this.isLegacyOptionsAvailable()) ||
      !(await this.confirmProjectUnlocked()) ||
      !(await this.switchLegacyFinalization())) {
      return;
    }

    this.close();
    this.projectComponentPresenter.showFinalizeLegacyProjectPopup();
  }

  public canGeneratePdmArticleNumber(): boolean {
    const hasPermission = UserUtils.hasFinalizeProjectsPermission(this.userModel.user);
    const isMcsGds = IsProjectType.mcsGds(this.projectModel.currentProjectType);
    const isLtl = IsProjectType.ltl(this.projectModel.currentProjectType);
    const is221 = IsProjectType.splicingConnector221(this.projectModel.currentProjectType);
    const articleNumberLongIsEmpty = !this.projectModel.currentProject.articleNumberLong
      || this.projectModel.currentProject.articleNumberLong.isEmpty();
    return hasPermission && (isMcsGds || isLtl || is221) && articleNumberLongIsEmpty;
  }

  private openChoiceActionManualArticleNoPopup(): void {
    const mcsGdsInitArticleNo: string =this.projectModel.currentInfoProperties.getString("mcsGdsInitArticleNo");
    this.projectComponentPresenter.openChoiceActionManualArticleNoPopup(mcsGdsInitArticleNo).then((useDefaultSeries) => {
      if (useDefaultSeries !== undefined || useDefaultSeries !== null) {
        this.generatePdmArticleNumber(useDefaultSeries);
      }
    });
  }

  private generatePdmArticleNumber(useDefaultSeries: boolean): void {
    this.projectDataService.generateArticleNumber(this.projectModel.currentProject, useDefaultSeries).subscribe((projectArticleInfo: ProjectArticleNumberInfoVO) => {
      if (projectArticleInfo.articleNumberLong) {
        this.setNewPdmProjectArticleNumber(projectArticleInfo);
        this.alertHelper.showAlert(
          'chooseFinalizationAction.title.generate.pdm.articleNo_success',
          'chooseFinalizationAction.message.generate.pdm.articleNo_success', {0: projectArticleInfo.articleNumberLong}, [AlertButton.OK], "info");
      } else {
        this.alertHelper.showAlert(
          'chooseFinalizationAction.title.generate.pdm.articleNo_failure',
          'chooseFinalizationAction.message.generate.pdm.articleNo_failure', null, [AlertButton.OK], "exclamation");
      }
      this.close();
    });
  }

  private setNewPdmProjectArticleNumber(projectArticleInfo: ProjectArticleNumberInfoVO): void {
   const currentProject: ProjectVO = this.projectModel.currentProject;
   currentProject.articleNumber = projectArticleInfo.articleNumber;
   currentProject.articleNumberLong = new ArticleNumberLong(projectArticleInfo.articleNumberLong);
   currentProject.articleNumberExtension = projectArticleInfo.drawingNumber;
  }

  private isLegacyOptionsAvailable(): boolean {
    if (this.projectModel && this.projectModel.currentProject) {
      return this.isLegacyOptionsAvailableByArticleNumberGenerated(this.projectModel.currentProject.isArticleNumberGenerated);
    }
    return false;
  }

  private isLegacyOptionsAvailableByArticleNumberGenerated(isArticleNumberGenerated: boolean): boolean {
    return !isArticleNumberGenerated;
  }

  public async executeFinalizeProjectWithoutPdmExport(): Promise<void> {
    if (!(await this.confirmProjectUnlocked()) ||
      !(await this.switchLegacyFinalization())) {
      return;
    }

    this.alertHelper.showAlert('finalizeProject.title.finalizeLegacyProjectConfirm', 'finalizeProject.message.finalizeLegacyProjectConfirm', null,
      [AlertButton.YES, AlertButton.NO], "error").then((answer) => {
        if (answer === AlertButton.YES) {
          // this.executeFinalizeProject();
          this.projectDataService.finalizeProjectWithoutPDM(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion).subscribe(
            (succes: string) => this.executeFinalizeProjectWithoutPdmExportSuccess(),
            (error: any) => this.finalizationErrorHandler(error)
          );
        }
      }
    );
  }

  private executeFinalizeProjectWithoutPdmExportSuccess(): void {
    this.projectService.updateProjectData(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion);
    this.alertHelper.showAlert('finalizeProject.title.legacyProjectFinalized', 'finalizeProject.message.legacyProjectFinalized', null, [AlertButton.OK], "exclamation");
    this.close();
  }

  public finalizationErrorHandler(error: any): void {
    this.projectService.updateProjectData(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion);
    this.alertHelper.showAlert('finalizeProject.title.error_finalizing_configuration', 'finalizeProject.message.error_finalizing_configuration', {'0' : error},
      [AlertButton.OK], "exclamation");
    this.close();
  }

  private async lockProject(): Promise<void> {
    if (!(await this.confirmProjectLocked())) {
      return;
    }

    this.projectDataService.lockProject(this.projectModel.currentProject.id).subscribe(
      (succes: boolean) => {
        this.alertHelper.showAlert(
        'chooseFinalizationAction.title.lock_project_success'
        , 'chooseFinalizationAction.message.lock_project_success', null, [AlertButton.OK], "info");
        this.projectModel.currentProject.isLocked = true;
        this.close();
      },
      (error: any) => this.alertHelper.showAlert(
        'global.title.error'
        , 'chooseFinalizationAction.message.lock_project_failure', null, [AlertButton.OK], "exclamation")
    );
  }

  private async unlockProject(): Promise<void> {
    if (!(await this.confirmProjectUnlocked())) {
      return;
    }

    this.projectDataService.unlockProject(this.projectModel.currentProject.id).subscribe(
      (succes: boolean) => {
        this.alertHelper.showAlert(
        'chooseFinalizationAction.title.unlock_project_success'
        , 'chooseFinalizationAction.message.unlock_project_success', null, [AlertButton.OK], "info");
        this.projectModel.currentProject.isLocked = false;
        this.close();
      },
      (error: any) => this.alertHelper.showAlert(
        'global.title.error'
        , 'chooseFinalizationAction.message.unlock_project_failure', null, [AlertButton.OK], "exclamation")
    );
  }




  private isConfigurationCancelled(): boolean {
    const outdated = ArticleUtils.isStatusOutdated(this.projectModel.currentProject.materialStatus);

    if (outdated) {
      this.alertHelper.showAlert('chooseFinalizationAction.title.project.outdated', 'chooseFinalizationAction.message.project.outdated', null,
        [AlertButton.OK], "error");
    }
    return outdated;
  }

  private async confirmProjectLocked(): Promise<Boolean> {
    if (this.projectModel.currentProject.isLocked) {
      return true;
    }

    const alertResult = await this.alertHelper.showAlert('chooseFinalizationAction.title.confirm_locking_project', 'chooseFinalizationAction.message.confirm_locking_project', null,
      [AlertButton.YES, AlertButton.NO], "error");

    return alertResult == AlertButton.YES;
  }

  private async confirmProjectUnlocked(): Promise<Boolean> {
    if (!this.projectModel.currentProject.isLocked) {
      return true;
    }

    const alertResult = await this.alertHelper.showAlert('chooseFinalizationAction.title.confirm_unlocking_project', 'chooseFinalizationAction.message.confirm_unlocking_project', null,
        [AlertButton.YES, AlertButton.NO], "error");

    return alertResult == AlertButton.YES;
  }


  private async updateProjectVersion(): Promise<Boolean> {
    const projectVersionDisplayString: string = ProjectUtils.getVersionDisplayString(this.projectModel.currentProject, false);
    if (!this._projectSaveInfo || !this. _projectSaveInfo.existsFinalizedProjectVersionDisplayString(projectVersionDisplayString)) {
      return true;
    }

    const buttonlabels = {
      noLabel: this.localeResourceManager.getValueByKey('global.label.ignore'),
      yesLabel : this.localeResourceManager.getValueByKey('projectRequestPopup.label.edit_project_version')
    };
    const projectVersionDisplayStringWithDate: string = ProjectUtils.getVersionDisplayString(this.projectModel.currentProject, true);
    const answer = await this.alertHelper.showAlert('projectRequestPopup.title.project_version_already_used', 'projectRequestPopup.message.project_version_already_used',  { 0: projectVersionDisplayStringWithDate },
    [AlertButton.YES, AlertButton.NO], "error", buttonlabels);

    if (answer === AlertButton.NO) {
      return true;
    } else if (answer === AlertButton.YES) {
      const result = await this.projectComponentPresenter.openEditProjectVersionComponent(this._projectSaveInfo);
      return result.isSuccess;
    }
    return false;
  }

  private async switchLegacyFinalization(): Promise<Boolean> {
    if (this.lastFinalizationState !==  ProjectStatesDef.FINALIZATION_STATE_LEGACY_PROJECT_EXPORT_DONE) {
      return true;
    }

    const alertResult = await this.alertHelper.showAlert('finalizeProject.title.switching_between_legacy_and_default_finalization', 'finalizeProject.message.switching_between_legacy_and_default_finalization', null,
        [AlertButton.YES, AlertButton.NO], "error");

    return alertResult == AlertButton.YES;
  }

}
